// helper to collect app routes and notify server about reserved top-level paths
// comments are kept lowercase per project style guide
import { BACKEND_URL } from "../config/env";

const BASE_URL = BACKEND_URL || "";
let sent = false;

function fileToRoute(file: string): string | null {
  // normalize to posix-style and strip prefix/suffix
  const rel = file
    .replace(/^\/src\/routes\//, "")
    .replace(/^src\/routes\//, "")
    .replace(/^\.\.\/routes\//, "")
    .replace(/^\.\/routes\//, "")
    .replace(/\.tsx$/, "");

  if (!rel || rel === "__root") return null;

  // convert tanstack file name to route path, eg editor.$id -> /editor/$id
  let path = "/" + rel.replace(/\./g, "/");
  if (path === "/index") path = "/";
  return path;
}

async function sha256Hex(input: ArrayBuffer | string) {
  const data =
    typeof input === "string"
      ? new TextEncoder().encode(input)
      : new Uint8Array(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(hash);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function base64url(bytes: Uint8Array) {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.toLowerCase().replace(/[^0-9a-f]/g, "");
  const len = clean.length;
  const out = new Uint8Array(len >> 1);
  for (let i = 0, j = 0; i < len; i += 2, j++) {
    const byte = parseInt(clean.slice(i, i + 2), 16);
    out[j] = Number.isFinite(byte) ? byte : 0;
  }
  return out;
}

async function deriveProofToken(
  svgHashHex: string,
  routesHashHex: string,
  step: number
) {
  const x = await sha256Hex(`${svgHashHex}:${routesHashHex}:${step}`);
  const y = await sha256Hex(`${routesHashHex}:${step}:${svgHashHex}`);
  const z = await sha256Hex(`${x}${y}`);
  const xb = hexToBytes(x);
  const yb = hexToBytes(y);
  const zb = hexToBytes(z);
  const out = new Uint8Array(xb.length);
  for (let i = 0; i < out.length; i++) {
    const v =
      (xb[i] ?? 0) ^
      (yb[(i * 7) % yb.length] ?? 0) ^
      (zb[(i * 13) % zb.length] ?? 0);
    const r = i % 5;
    out[i] = ((v << r) | (v >>> (8 - r))) & 0xff;
  }
  return base64url(out);
}

export async function sendReservedRoutes(force = false) {
  if (sent && !force) return;
  try {
    // vite will expose virtual module keys for matched files
    const routeFiles = Object.keys(import.meta.glob("../routes/**/*.tsx"));

    const paths = new Set<string>();
    for (const file of routeFiles) {
      const route = fileToRoute(file);
      if (!route) continue;
      paths.add(route);
    }

    if (paths.size === 0) return;

    let svgHashHex;
    try {
      const svgRes = await fetch("/vite.svg", { credentials: "omit" });
      if (svgRes.ok) {
        const svgBuf = await svgRes.arrayBuffer();
        svgHashHex = (await sha256Hex(svgBuf)).toLowerCase();
      }
    } catch {}
    if (!svgHashHex) return;

    // derive proof string from svg hash + time step + routes hash
    const routeList = Array.from(paths);
    routeList.sort();
    const routesHashHex = await sha256Hex(routeList.join("\n"));
    const step = Math.floor(Date.now() / 30000);
    const proof = await deriveProofToken(svgHashHex, routesHashHex, step);

    // build compact token with routes and proof
    const payload = { r: routeList, p: proof };
    const token = base64url(new TextEncoder().encode(JSON.stringify(payload)));

    const res = await fetch(`${BASE_URL}/api/health`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!res.ok) {
      throw new Error("failed to send health check");
    }

    sent = true;
  } catch (err) {
    console.error("failed to send health check", err);
  }
}

// lazy trigger utility: call after auth session known or after idle timeout
export function scheduleReservedRoutes(getSession: () => any) {
  if (sent) return;
  const s = getSession();
  if (s && !sent) {
    void sendReservedRoutes();
    return;
  }
  // fallback: delay for anonymous users to avoid early roundtrip
  setTimeout(() => {
    if (!sent) void sendReservedRoutes();
  }, 8000);
}
